(() => {
  const SECTION_SELECTOR = '[data-kalaboost-defilment-bar]';
  const MARQUEE_SELECTOR = '[data-kalaboost-defilment-marquee]';
  const TRACK_SELECTOR = '[data-kalaboost-defilment-track]';
  const ORIGINAL_GROUP_SELECTOR = '[data-kalaboost-defilment-group-original]';
  const CLONE_SELECTOR = '[data-kalaboost-defilment-group-clone]';

  const sectionState = new WeakMap();

  const prefersReducedMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const clearClones = (track) => {
    track.querySelectorAll(CLONE_SELECTOR).forEach((clone) => clone.remove());
  };

  const buildClones = (section) => {
    const marquee = section.querySelector(MARQUEE_SELECTOR);
    const track = section.querySelector(TRACK_SELECTOR);
    const original = section.querySelector(ORIGINAL_GROUP_SELECTOR);

    if (!marquee || !track || !original) return 0;

    clearClones(track);

    if (prefersReducedMotion()) {
      return 0;
    }

    const setWidth = Math.ceil(original.offsetWidth);

    if (setWidth <= 0) return 0;

    const minTrackWidth = Math.max(marquee.offsetWidth * 2, setWidth * 2);

    const addClone = () => {
      const clone = original.cloneNode(true);
      clone.removeAttribute('data-kalaboost-defilment-group-original');
      clone.setAttribute('data-kalaboost-defilment-group-clone', '');
      clone.setAttribute('aria-hidden', 'true');
      clone.classList.add('kalaboost-defilment-bar__group--duplicate');
      track.appendChild(clone);
    };

    addClone();

    let safety = 0;
    while (track.scrollWidth < minTrackWidth && safety < 20) {
      addClone();
      safety += 1;
    }

    return setWidth;
  };

  const updateSection = (section) => {
    if (!(section instanceof HTMLElement)) return;

    const track = section.querySelector(TRACK_SELECTOR);

    if (!track) return;

    track.removeAttribute('data-ready');
    track.style.removeProperty('transform');

    const setWidth = buildClones(section);

    if (setWidth <= 0) {
      requestAnimationFrame(() => {
        const retryWidth = buildClones(section);
        if (retryWidth > 0 && !prefersReducedMotion()) {
          track.style.setProperty(
            '--kalaboost-defilment-bar-scroll-distance',
            `${retryWidth}px`
          );
          track.setAttribute('data-ready', '');
        }
      });
      return;
    }

    if (prefersReducedMotion()) return;

    track.style.setProperty(
      '--kalaboost-defilment-bar-scroll-distance',
      `${setWidth}px`
    );
    track.setAttribute('data-ready', '');
  };

  const stopSection = (section) => {
    const state = sectionState.get(section);
    if (!state) return;

    window.removeEventListener('resize', state.onResize);
    clearTimeout(state.resizeTimer);
    sectionState.delete(section);
  };

  const initSection = (section) => {
    if (!(section instanceof HTMLElement)) return;

    updateSection(section);

    if (sectionState.has(section)) return;

    let resizeTimer = null;

    const onResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        updateSection(section);
      }, 200);
    };

    window.addEventListener('resize', onResize, { passive: true });

    if (document.fonts && document.fonts.status !== 'loaded') {
      document.fonts.ready.then(() => {
        updateSection(section);
      });
    }

    sectionState.set(section, { onResize, resizeTimer });
  };

  const initAll = (root = document) => {
    if (!(root instanceof Element || root instanceof Document)) return;

    const sections = new Set();

    if (root instanceof Element && root.matches(SECTION_SELECTOR)) {
      sections.add(root);
    }

    root.querySelectorAll(SECTION_SELECTOR).forEach((section) => {
      sections.add(section);
    });

    sections.forEach(initSection);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (!(event.target instanceof Element)) return;
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    if (!(event.target instanceof Element)) return;

    if (event.target.matches(SECTION_SELECTOR)) {
      stopSection(event.target);
    }

    event.target.querySelectorAll(SECTION_SELECTOR).forEach(stopSection);
  });
})();
